import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import { categories, articles } from "@/mocks/data";

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(slug || "all");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const filteredArticles =
    activeCategory === "all"
      ? articles
      : articles.filter((a) => {
          const cat = categories.find((c) => c.slug === activeCategory);
          return cat ? a.categoryId === cat.id : true;
        });

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    if (newSlug === "all") {
      navigate("/category/cross-border-business");
    } else {
      navigate(`/category/${newSlug}`);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const breadcrumbItems = [
    { label: t("breadcrumb.home"), path: "/" },
    { label: t("breadcrumb.articles") },
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: t("breadcrumb.home"),
        item: "https://www.1996zhcheng.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: t("breadcrumb.articles"),
        item: `https://www.1996zhcheng.com/category/${activeCategory}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={t("seo.categories.title")}
        description={t("seo.categories.description")}
        keywords={t("seo.categories.keywords")}
        canonicalUrl={`https://www.1996zhcheng.com/category/${activeCategory}`}
        schemaJson={[breadcrumbSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 md:pt-24">
          <div className="container-page">
            <Breadcrumb items={breadcrumbItems} />

            <div className="mb-8">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-3">
                {t("nav.articles")}
              </h1>
              <p className="text-sm md:text-base text-foreground-600">
                {t("seo.categories.description")}
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8">
              <button
                onClick={() => handleCategoryChange("all")}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeCategory === "all"
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {t("category.all")}
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    activeCategory === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {currentCategory && activeCategory !== "all" && (
              <div className="p-6 rounded-xl bg-accent-50 border border-accent-200/50 mb-8 flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-500 text-background-50 shrink-0">
                  <i className={`${currentCategory.icon} text-lg`}></i>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground-900 mb-1">{currentCategory.name}</h2>
                  <p className="text-sm text-foreground-600">{currentCategory.description}</p>
                </div>
              </div>
            )}

            {/* Articles List */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-16">
              {filteredArticles.map((article) => (
                <button
                  key={article.id}
                  onClick={() => {
                    navigate(`/article/${article.id}`);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="group w-full text-left p-5 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col sm:flex-row gap-4"
                >
                  <div className="w-full sm:w-44 h-32 rounded-lg overflow-hidden shrink-0">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      title={article.title}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-accent-50 text-accent-600">
                        {article.categoryName}
                      </span>
                      <span className="text-xs text-foreground-400">{article.publishDate}</span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.excerpt}</p>
                    <div className="flex items-center gap-1 mt-3 text-xs text-foreground-400">
                      <i className="ri-eye-line"></i>
                      <span>{article.readCount.toLocaleString()} 阅读</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}